import { CutoffWindow, Schedule } from './types'

/**
 * Converts a 24hr integer (e.g. 1400) to an HH:mm string for <input type="time">.
 */
export function formatCutoffTime(time: number): string {
  const hours = Math.floor(time / 100)
  const minutes = time % 100

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`
}

/**
 * Parses an HH:mm string from <input type="time"> back to a 24hr integer.
 */
export function parseCutoffTimeInput(value: string): number {
  const [hours, minutes] = value.split(':').map(Number)

  return (hours || 0) * 100 + (minutes || 0)
}

/**
 * Formats a CutoffWindow into a short display label like "1d@2pm" or "0d@10am".
 */
export function formatCutoffLabel(cutoff: CutoffWindow): string {
  return `${cutoff.cutoffDaysBefore}d@${format12hr(cutoff.cutoffTime)}`
}

/**
 * Formats a 24hr integer into a short 12hr label like "2pm" or "10:30am".
 */
export function format12hr(time: number): string {
  const hours24 = Math.floor(time / 100)
  const minutes = time % 100
  const ampm = hours24 >= 12 ? 'pm' : 'am'
  const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24
  const minuteStr = minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''

  return `${hours12}${minuteStr}${ampm}`
}

/**
 * Formats a dropoff time (24hr integer) into a short label like "11:30am".
 */
export function formatDropoffLabel(time: number): string {
  return format12hr(time)
}

/**
 * Returns an ISO date string (YYYY-MM-DD) for a Date object.
 */
export function toISODate(date: Date): string {
  const y = date.getFullYear()
  const m = (date.getMonth() + 1).toString().padStart(2, '0')
  const d = date.getDate().toString().padStart(2, '0')

  return `${y}-${m}-${d}`
}

/**
 * Validates that a list of schedules has no overlapping effective date ranges.
 * Returns an error message if overlap found, null otherwise.
 */
export function validateNoOverlap(schedules: Schedule[]): string | null {
  const sorted = [...schedules].sort((a, b) =>
    a.effectiveFrom.localeCompare(b.effectiveFrom),
  )
  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i]
    const next = sorted[i + 1]
    // If current has no end date, it overlaps with everything after it
    if (current.effectiveUntil === null) {
      return `Schedule effective from ${current.effectiveFrom} is open-ended and overlaps with schedule starting ${next.effectiveFrom}. Set an end date first.`
    }
    if (current.effectiveUntil >= next.effectiveFrom) {
      return `Schedule ${current.effectiveFrom}–${current.effectiveUntil} overlaps with schedule starting ${next.effectiveFrom}.`
    }
  }

  return null
}
